import { useTranslation } from 'react-i18next';

import { Switch } from '@/components/atoms/Switch';
import Tooltip from '@/components/atoms/Tooltip';
import { ChartHeaderActions } from '@/components/charts/actions';
import { MetricChart } from '@/components/charts/generic';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { RetryProps } from '@/components/retryFailedQueries';
import type { SwimSpeedDataPoint, ThresholdRange } from '@/domain/behavior';
import type { ApiError } from '@/domain/shared/errors';

import { useOverviewSwimSpeedChartData } from '../../hooks/useOverviewSwimSpeedChartData';

interface SwimSpeedOverviewChartProps {
    dateRange: DateRange;
    onDateChange: (data: DateRange) => void;
    swimSpeedDataPoints: SwimSpeedDataPoint[];
    swimSpeedThreshold: ThresholdRange;
    isLoading: boolean;
    error: ApiError | null;
    /** Try again in the chart's error state. */
    retry?: RetryProps;
}

export default function SwimSpeedOverviewChart(props: SwimSpeedOverviewChartProps) {
    const {
        dateRange,
        isLoading = false,
        error,
        retry,
        onDateChange,
        swimSpeedDataPoints,
        swimSpeedThreshold,
    } = props;
    const { t } = useTranslation('behaviorModule');

    const { chart, showOnlyActiveSwimSpeed, setShowOnlyActiveSwimSpeed } =
        useOverviewSwimSpeedChartData({
            swimSpeedDataPoints,
            swimSpeedThreshold,
        });

    const legendRight = (
        <Tooltip layout="fit" content={t('tooltips.viewOnlyActiveSwimSpeed')}>
            <Switch
                label={t('overview.viewOnlyActiveSwimSpeed')}
                checked={showOnlyActiveSwimSpeed}
                onCheckedChange={setShowOnlyActiveSwimSpeed}
                labelPosition="left"
            />
        </Tooltip>
    );

    return (
        <ContentBlock
            title={t('swimSpeedView.title')}
            containerRef={chart.fullScreenContainerRef}
            headerActions={
                <ChartHeaderActions
                    chartRef={chart.chartRef}
                    chartName="swim-speed-chart"
                    csvExport={false}
                    isFullScreen={chart.isFullScreen}
                    onFullScreenToggle={chart.handleFullScreenToggle}
                    dateFrom={dateRange.dateFrom}
                    dateTo={dateRange.dateTo}
                    onDateChange={onDateChange}
                    defaultPresetId="last7days"
                    disabled={isLoading}
                    show24hPreset={true}
                />
            }>
            <MetricChart
                legendRowLayout="centered"
                kind="line"
                data={chart.data}
                options={chart.options}
                legendItems={chart.legendItems}
                visibleSeries={chart.visibleSeries}
                onToggleSeries={chart.toggleSeries}
                legendRight={legendRight}
                chartRef={chart.chartRef}
                height={chart.isFullScreen ? 400 : 230}
                isLoading={isLoading}
                error={error}
                onRetry={retry?.onRetry}
                isRetrying={retry?.isRetrying}
            />
        </ContentBlock>
    );
}
